import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/shared/AuthContext';
import apiService from '../services/shared/apiService';
import { Patient } from '../shared/types/src';
import PageContainer from '../components/common/PageContainer';
import PageSection from '../components/common/PageSection';
import RadarTrendGraph from '../components/therapist/insights/RadarTrendGraph';
import TagTrendPanel from '../components/therapist/insights/TagTrendPanel';
import AlertHistoryPanel from '../components/therapist/insights/AlertHistoryPanel';
import CohortSummaryPanel from '../components/therapist/insights/CohortSummaryPanel';
import PromptLog from '../components/therapist/insights/PromptLog';

const TherapistInsights: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedPatientId, setSelectedPatientId] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPatients = async () => {
      if (!user) return;
      try {
        setIsLoading(true);
        const response = await apiService.getTherapistPatients(user.uid);
        setPatients(response.patients);
        
        const patientIdFromUrl = searchParams.get('patientId');
        if (patientIdFromUrl && response.patients.some(p => p.uid === patientIdFromUrl)) {
          setSelectedPatientId(patientIdFromUrl);
        } else if (response.patients.length > 0) {
          setSelectedPatientId(response.patients[0].uid);
        }
        
      } catch (err) {
        setError('Failed to load patient data.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPatients();
  }, [user, searchParams]);

  const handlePatientChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newPatientId = event.target.value;
    setSelectedPatientId(newPatientId);
    setSearchParams({ patientId: newPatientId });
  };

  if (isLoading) {
    return <PageContainer><PageSection title="Therapist Insights" emoji="🧠"><p>Loading insights...</p></PageSection></PageContainer>;
  }

  if (error) {
    return <PageContainer><PageSection title="Therapist Insights" emoji="🧠"><p className="text-red-500">{error}</p></PageSection></PageContainer>;
  }

  return (
    <PageContainer>
      <PageSection title="Therapist Insights Dashboard" emoji="🧠">
        <div className="mb-6">
          <label htmlFor="patient-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select Patient
          </label>
          <select
            id="patient-select"
            value={selectedPatientId}
            onChange={handlePatientChange}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            {patients.map(p => (
              <option key={p.uid} value={p.uid}>{p.name}</option>
            ))}
          </select>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Main Column */}
          <div className="space-y-6 lg:col-span-2">
            <CohortSummaryPanel patients={patients} />
          </div>

          {selectedPatientId && (
            <>
              <div className="lg:col-span-2">
                <RadarTrendGraph patientId={selectedPatientId} />
              </div>
              <TagTrendPanel patientId={selectedPatientId} />
              <AlertHistoryPanel patientId={selectedPatientId} />
              <div className="lg:col-span-2">
                <PromptLog patientId={selectedPatientId} />
              </div>
            </>
          )}

        </div>
      </PageSection>
    </PageContainer>
  );
};

export default TherapistInsights; 